import React from "react"
import * as graphql from "lib/types/generated/graphql-types"
import {
  OrderPayment,
  OrderPaymentKlarnaCheckout,
  OrderPaymentQliroOne
} from "lib/types/generated/graphql-types"
import { v4 as uuid } from "uuid"

import { StyledOrderLines, Status } from "./OrderLines.styled"
import { Orderline } from "../../../../../components/Order/OrderLine/OrderLine"
import { GiftLine } from "../../../../../components/Order/OrderLine/GiftLine"
import { GiftCardProductLine } from "../../../../../components/Order/OrderLine/GiftCardProductLine"
import UpsellOrderLine from "../UpsellOrderLine"
import { isOrderGiftCardProductRetain24 } from "../../../../../components/Order/helpers"
import { OrderHistoryOperations } from "../../../../../classes/order/OrderHistoryOperations"

type Props = {
  order: graphql.Order
  orderHistory: graphql.OrderHistory
}

export const OrderLines = ({ order, orderHistory }: Props) => {
  const { orderLines, gifts, GiftCardProduct } = order

  const orderOperations = new OrderHistoryOperations(orderHistory?.operations)

  const deliveredOrderLine = (orderLineId: string) =>
    !!orderOperations.getDeliveredOrderLine(orderLineId)

  const placedOrderLines = orderLines?.filter((ol) => !deliveredOrderLine(ol.id))
  const deliveredOrderLines = orderLines?.filter((ol) => deliveredOrderLine(ol.id))

  function isOrderPaymentQliroOne(object: OrderPayment): object is OrderPaymentQliroOne {
    return object.providerName === "QliroOne"
  }

  function isOrderPaymentKlarnaCheckout(object: OrderPayment): object is OrderPaymentKlarnaCheckout {
    return object.providerName === "KlarnaCheckout"
  }

  const upsellOrderLines = () => {
    if (isOrderPaymentQliroOne(order.Payment) || isOrderPaymentKlarnaCheckout(order.Payment)) {
      const orderPayment = order.Payment
      const upsellOrderLines = order?.orderLines?.filter((ol) =>
        orderPayment?.upsell?.items?.some(
          (item) => ol.productVariantId === item.productVariantId && ol.quantity === item.quantity
        )
      )
      return upsellOrderLines ?? []
    }
    return []
  }

  const showUpsellOrderLines = () => {
    if (upsellOrderLines().length > 0) {
      return (
        upsellOrderLines().map((ol) => ol.productVariantId).length ===
        new Set(upsellOrderLines().map((ol) => ol.productVariantId)).size
      )
    }
    return false
  }

  const upsellOnOrderLine = (orderLineId: string) => {
    if (showUpsellOrderLines()) {
      return upsellOrderLines()
        .map((ol) => ol.id)
        .includes(orderLineId)
    }
    return false
  }

  const displayUpsellSeparate = upsellOrderLines().length > 0 && !showUpsellOrderLines()

  return (
    <>
      <StyledOrderLines>
        {placedOrderLines && placedOrderLines?.length > 0 && (
          <>
            {placedOrderLines.map((orderLine: graphql.OrderLine) => (
              <Orderline
                key={uuid()}
                order={order}
                orderLine={orderLine}
                orderHistory={orderHistory}
                upsell={upsellOnOrderLine(orderLine.id)}
              />
            ))}
          </>
        )}
        {deliveredOrderLines && deliveredOrderLines?.length > 0 && (
          <>
            <Status>Delivered</Status>
            {deliveredOrderLines.map((orderLine: graphql.OrderLine) => (
              <Orderline
                key={uuid()}
                order={order}
                orderLine={orderLine}
                orderHistory={orderHistory}
                upsell={upsellOnOrderLine(orderLine.id)}
              />
            ))}
          </>
        )}
        {isOrderGiftCardProductRetain24(GiftCardProduct) && (
          <>
            {GiftCardProduct?.giftCardProducts?.map((product) => (
              <GiftCardProductLine
                key={product.id}
                giftCardProductLine={product}
                order={order}
                orderHistory={orderHistory}
              />
            ))}
          </>
        )}
        {gifts && gifts?.length > 0 && (
          <>
            {gifts &&
              gifts.map((gift: graphql.OrderGift) => (
                <GiftLine key={uuid()} gift={gift} order={order} orderHistory={orderHistory} />
              ))}
          </>
        )}
      </StyledOrderLines>
      {displayUpsellSeparate && isOrderPaymentQliroOne(order.Payment) && (
        <>
          <h3>Purchased with upsell: </h3>
          {order?.Payment?.upsell?.items.map((item) => <UpsellOrderLine upsellItem={item} />)}
        </>
      )}
    </>
  )
}
